import {inject} from '@angular/core';
import {CanActivateChildFn} from '@angular/router';
import {UserService as MwUserService} from '@middleware';
import {AttestationService} from '@services/attestation/attestation.service';
import {UserService} from '@services/user/user.service';
import {lastValueFrom} from 'rxjs';

export const PDE_EULA_SYM: unique symbol = Symbol('PDE License Attestation Sentinel');

/**
 * Returns true if the user has accepted the EULA.
 * Otherwise, displays the EULA dialog.
 *
 * If the user accepts, returns true.
 * If the user declines, returns false and cancels navigation.
 */
export const privateDataEnclaveEulaGuard: CanActivateChildFn = async (childRoute, _state) => {
  const userService = inject(UserService);
  const attestationService = inject(AttestationService);
  const mwUserService = inject(MwUserService);

  const user = await userService.userPromise;

  // The VPN check should have checked for the user before they got here, but just in case...
  if (!user?.organization?.data_enclave_url) {
    return false;
  }

  // The user has already accepted the EULA, so we can allow them to proceed.
  if (!!user?.eula_accepted_at) {
    return true;
  }

  // They haven't accepted the EULA. Display the EULA dialog and ask them to accept it before proceeding.
  const confirmed = await attestationService.confirmWithCancel(PDE_EULA_SYM);

  if (!confirmed) return false;

  // Save changes to user.
  await lastValueFrom(mwUserService.patchUser('me', {eula_accepted_at: new Date().toISOString()}));

  // Update the user in the UserService so that other parts of the app know that the EULA has been accepted.
  await userService.reloadUser();
  return true;
};
